import React, { useEffect } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/assetsLiabilities";
import { useAddAssetsLiabilities } from "app/hooks/assetsLiabilities/useAddAssetsLiabilities";
import { ASSST_LIABILITY } from "app/constants/apiEndPoints";
import toast from "react-hot-toast";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import RadioButton from "app/components/common/buttons/RadioButton";
import AddMore from "app/components/common/buttons/AddMore";
import { useDeleteItem } from "app/hooks/useDeleteItem";
import DynamicCarLaon from "app/components/Individual/applicant/assetsLiabilities/DynamicCarLaon";
import { handleApiError } from "app/utils/handleApiError";

interface otherassetsInter {
  lenderName: string;
  balanceOwing: string;
  minMonthlyPayment: string;
  toBeRefinanced: boolean;
  noOfTitleHolders: string;
  dbId?: string;
}

export interface AssetsLiabilitiesPartThreeFormData {
  carloanType: string;
  carLoan: otherassetsInter[];
}

const CarLoan: React.FC = () => {
  const { mutate: deleteItem, isPending: deleteIsPending } = useDeleteItem();
  const dispatch = useDispatch();
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );
  const { mutate: submitAssetsLiabilities, isPending } =
    useAddAssetsLiabilities();
  const { formThree, assetLiabilityId, ...others } = useSelector(
    (state: RootState) => state.assetsLiabilities.stepsData
  ) || {
    formThree: { carLoan: [] },
  };
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<AssetsLiabilitiesPartThreeFormData>({
    defaultValues: {
      carLoan: [
        {
          lenderName: "",
          balanceOwing: "",
          minMonthlyPayment: "",
          noOfTitleHolders: "",
          toBeRefinanced: false,
        },
      ],
      carloanType: "no",
    },
  });

  const carloanValue = watch("carloanType");

  const onSubmit = (data: AssetsLiabilitiesPartThreeFormData) => {
    if (!savedIds?.applicantID) {
      toast.error("Applicant id is required");
      return;
    }
    const { carLoan, carloanType } = data || {};
    const payload: any = {};
    if (carloanType === "yes") {
      payload.carLoan = carLoan.map((item) => {
        const obj: any = {
          lenderName: item.lenderName,
          toBeRefinanced: item.toBeRefinanced,
          balanceOwing: Number(item.balanceOwing),
          minMonthlyPayment: Number(item.minMonthlyPayment),
          noOfTitleHolders: Number(item.noOfTitleHolders),
        };
        if (item.dbId) {
          obj.id = item.dbId;
        }
        return obj;
      });
    } else {
      payload.carLoan = null;
    }

    submitAssetsLiabilities(
      {
        url: `${ASSST_LIABILITY}/${savedIds?.applicantID}/fourth`,
        data: payload,
      },
      {
        onSuccess: (data: any) => {
          const { carLoan } = data || {};
          const carLoanMapped = carLoan?.map((item: any) => ({
            dbId: item.id,
            lenderName: item.lenderName,
            balanceOwing: item.balanceOwing,
            minMonthlyPayment: item.minMonthlyPayment,
            toBeRefinanced: item.toBeRefinanced,
            noOfTitleHolders: String(item.noOfTitleHolders),
          }));

          dispatch(
            addStepsData({
              ...others,
              formThree: {
                ...formThree,
                carLoan: carloanType === "no" ? [] : carLoanMapped,
                carloanType,
              },
            })
          );

          dispatch(nextStep());
        },
        onError: (error: any) => {
          handleApiError(error);
        },
      }
    );
  };

  const {
    fields: superannuationFields,
    append: superannuationAppend,
    remove: superannuationRemove,
  } = useFieldArray({
    control,
    name: "carLoan",
  });

  useEffect(() => {
    if (
      formThree &&
      formThree?.existingMortgageType &&
      formThree?.carloanType
    ) {
      const updatedCarLoan =
        formThree.carloanType === "no"
          ? [
              {
                lenderName: "",
                balanceOwing: "",
                minMonthlyPayment: "",
                noOfTitleHolders: "",
                toBeRefinanced: false,
              },
            ]
          : formThree.carLoan?.map((item: any) => ({
              ...item,
              noOfTitleHolders:
                item.noOfTitleHolders !== undefined &&
                item.noOfTitleHolders !== null
                  ? String(item.noOfTitleHolders)
                  : "",
            }));

      reset({
        ...(formThree.carloanType && { carloanType: formThree.carloanType }),
        carLoan: updatedCarLoan,
      });
    }
  }, [formThree, reset]);

  return (
    <>
      <ProgressBar progress={36} MB={7} />
      <div className="w-full  sm:w-[80%] xl:w-[60%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Car loan" />
        <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
          <div className="text-left mb-5">
            <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
              Do you have any personal/car loan?
            </h6>
            <div className="flex gap-5">
              <Controller
                name="carloanType"
                control={control}
                rules={{ required: "Please select an option" }}
                render={({ field: { value, onChange } }) => (
                  <div className="flex gap-5">
                    <RadioButton
                      id="carloan_yes"
                      name="carloanType"
                      value="yes"
                      label="Yes"
                      checked={value === "yes"}
                      onChange={onChange}
                    />
                    <RadioButton
                      id="carloan_no"
                      name="carloanType"
                      value="no"
                      label="No"
                      checked={value === "no"}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
            </div>
            {errors.carloanType && (
              <ErrorsMessage
                title={errors.carloanType.message}
                className="!text-left"
              />
            )}
          </div>

          {carloanValue === "yes" && (
            <>
              <DynamicCarLaon
                control={control}
                errors={errors}
                fields={superannuationFields}
                deleteItem={deleteItem}
                carLoan="carLoan"
                applicantID={savedIds?.applicantID as string}
                remove={superannuationRemove}
                deleteIsPending={deleteIsPending}
              />
              {superannuationFields.length < 3 && (
                <div className="flex items-center justify-end mt-6">
                  <AddMore
                    onClick={() =>
                      superannuationAppend({
                        lenderName: "",
                        balanceOwing: "",
                        minMonthlyPayment: "",
                        noOfTitleHolders: "",
                        toBeRefinanced: false,
                      })
                    }
                  />
                </div>
              )}
            </>
          )}

          <div className="flex justify-between my-10">
            <PreviousButtom onClick={() => dispatch(prevStep())}>
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default CarLoan;
